import React from 'react';
import styled, { css, keyframes } from 'styled-components';

export type JRCDisplayCardProps = {
    children?: React.ReactNode;
    loading?: boolean;
    skeleton?: {
        width?: string;
        height?: string;
        aspectRatio?: number;
    };
    width?: string;
    height?: string;
    aspectRatio?: number;
    href?: string;
    onClick?: () => void;
};

const SkeletonAnim = (color1: string, color2: string) => keyframes`
    0%, 100% {
        background-color: ${color1};
    }
    50% {
        background-color: ${color2};
    }
`;

const Card = (props: Pick<JRCDisplayCardProps, 'loading' | 'skeleton' | 'width' | 'height' | 'aspectRatio'>) => css`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.space.sm}px;
    background-color: ${({ theme }) => theme.color.grey0};
    border-radius: ${({ theme }) => theme.space.sm}px;
    border: 1px solid ${({ theme }) => theme.color.grey1};
    flex-wrap: wrap;
    width: ${(props.loading && props.skeleton ? props.skeleton.width : props.width) ?? 'initial'};
    height: ${(props.loading && props.skeleton ? props.skeleton.height : props.height) ?? 'initial'};
    aspect-ratio: ${(props.loading && props.skeleton ? props.skeleton.aspectRatio : props.aspectRatio) ?? 'initial'};
    animation: ${({ theme }) => (props.loading ? SkeletonAnim(theme.color.white, theme.color.grey1) : 'none')} 3s
        infinite;
`;

const Hover = css`
    &:hover {
        background-color: ${({ theme }) => theme.color.grey1};
        border: 1px solid ${({ theme }) => theme.color.grey2};
        cursor: pointer;
    }
`;

const CardDiv = styled.div<Pick<JRCDisplayCardProps, 'loading' | 'skeleton' | 'width' | 'height' | 'aspectRatio'>>`
    ${(props) => Card(props)}
    ${({ onClick }) => onClick && Hover}
`;

const CardLink = styled.a<Pick<JRCDisplayCardProps, 'loading' | 'skeleton' | 'width' | 'height' | 'aspectRatio'>>`
    ${(props) => Card(props)}
    ${Hover}
`;

export const JRCDisplayCard = ({ children, href, ...props }: JRCDisplayCardProps) => {
    if (href) {
        return (
            <CardLink {...props} href={href}>
                {children}
            </CardLink>
        );
    }
    return <CardDiv {...props}>{children}</CardDiv>;
};
